import { useState } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  ListInput,
  Toggle,
  Dialog,
  DialogButton,
} from 'konsta/react'
import {
  Power,
  Trash2,
  RefreshCw,
  Database,
  HardDrive,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react'
import { formatRelativeTime, cn } from '@/lib/utils'

// --- Types ---

interface MaintenanceAction {
  id: string
  label: string
  description: string
  icon: React.ElementType
  color: string
  confirmMessage: string
}

interface MaintenanceHistory {
  id: string
  action: string
  date: Date
  duration: string
  status: 'completed' | 'failed'
}

// --- Mock Data ---

const systemActions: MaintenanceAction[] = [
  {
    id: 'clear-cache',
    label: '清除快取',
    description: '清除 Redis 快取資料',
    icon: Trash2,
    color: '#FF9500',
    confirmMessage: '確定要清除所有快取資料嗎？這可能會暫時影響系統效能。',
  },
  {
    id: 'restart-ws',
    label: '重啟 WebSocket',
    description: '重啟 WebSocket 連線服務',
    icon: RefreshCw,
    color: '#007AFF',
    confirmMessage: '確定要重啟 WebSocket 服務嗎？所有玩家的連線將被短暫中斷。',
  },
  {
    id: 'rebuild-index',
    label: '重建索引',
    description: '重建資料庫索引以優化查詢',
    icon: Database,
    color: '#AF52DE',
    confirmMessage: '確定要重建資料庫索引嗎？過程中可能會影響查詢效能。',
  },
  {
    id: 'backup-db',
    label: '備份資料庫',
    description: '建立完整資料庫備份',
    icon: HardDrive,
    color: '#34C759',
    confirmMessage: '確定要立即備份資料庫嗎？這可能需要幾分鐘時間。',
  },
]

const mockHistory: MaintenanceHistory[] = [
  { id: '1', action: '資料庫備份', date: new Date(Date.now() - 1000 * 60 * 60 * 6), duration: '3 分鐘', status: 'completed' },
  { id: '2', action: '清除快取', date: new Date(Date.now() - 1000 * 60 * 60 * 24), duration: '15 秒', status: 'completed' },
  { id: '3', action: '重建索引', date: new Date(Date.now() - 1000 * 60 * 60 * 48), duration: '8 分鐘', status: 'completed' },
]

// --- Component ---

export function AdminMaintenanceScreen() {
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [maintenanceMessage, setMaintenanceMessage] = useState('系統維護中，請稍後再試。')
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false)
  const [showActionDialog, setShowActionDialog] = useState(false)
  const [selectedAction, setSelectedAction] = useState<MaintenanceAction | null>(null)

  const handleMaintenanceToggle = () => {
    if (!maintenanceMode) {
      // Turning ON - show confirmation
      setShowMaintenanceDialog(true)
    } else {
      // Turning OFF - just toggle
      setMaintenanceMode(false)
      console.log('[Admin] Maintenance mode disabled')
    }
  }

  const handleMaintenanceConfirm = () => {
    setMaintenanceMode(true)
    setShowMaintenanceDialog(false)
    console.log('[Admin] Maintenance mode enabled')
  }

  const handleActionClick = (action: MaintenanceAction) => {
    setSelectedAction(action)
    setShowActionDialog(true)
  }

  const handleActionConfirm = () => {
    if (selectedAction) {
      console.log(`[Admin] Executing: ${selectedAction.id}`)
    }
    setShowActionDialog(false)
    setSelectedAction(null)
  }

  return (
    <>
      <Navbar title="系統維護" />

      {/* Maintenance Mode Toggle */}
      <BlockTitle>維護模式</BlockTitle>
      <List strongIos insetIos>
        <ListItem
          media={
            <div
              className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center',
                maintenanceMode ? 'bg-[#FF3B30]/15' : 'bg-[#34C759]/15',
              )}
            >
              <Power
                className="w-5 h-5"
                style={{ color: maintenanceMode ? '#FF3B30' : '#34C759' }}
              />
            </div>
          }
          title={<span className="font-medium">維護模式</span>}
          subtitle={
            <span className={cn('text-[12px]', maintenanceMode ? 'text-[#FF3B30]' : 'text-[#8E8E93]')}>
              {maintenanceMode ? '已啟用 - 玩家無法進入遊戲' : '已停用 - 系統正常運行'}
            </span>
          }
          after={
            <Toggle
              checked={maintenanceMode}
              onChange={handleMaintenanceToggle}
            />
          }
        />
      </List>

      {/* Maintenance Message */}
      <BlockTitle>維護訊息</BlockTitle>
      <List strongIos insetIos>
        <ListInput
          type="textarea"
          placeholder="輸入維護訊息..."
          value={maintenanceMessage}
          onInput={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setMaintenanceMessage(e.target.value)
          }
          inputClassName="!min-h-[80px]"
        />
      </List>

      {/* Scheduled Maintenance */}
      <BlockTitle>排程維護</BlockTitle>
      <Block className="!px-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#007AFF]/15 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#007AFF]" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-black">下次排程維護</p>
              <p className="text-sm text-[#8E8E93]">2026/02/15 (六) 03:00 - 05:00 UTC</p>
            </div>
          </div>
          <div className="mt-3 p-3 bg-[#F2F2F7] rounded-xl">
            <p className="text-[13px] text-[#8E8E93]">
              內容：資料庫升級、安全性更新
            </p>
          </div>
        </div>
      </Block>

      {/* System Actions */}
      <BlockTitle>系統操作</BlockTitle>
      <List strongIos insetIos>
        {systemActions.map((action) => {
          const Icon = action.icon
          return (
            <ListItem
              key={action.id}
              link
              chevronIos
              onClick={() => handleActionClick(action)}
              media={
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${action.color}15` }}
                >
                  <Icon className="w-5 h-5" style={{ color: action.color }} />
                </div>
              }
              title={<span className="font-medium">{action.label}</span>}
              subtitle={
                <span className="text-[#8E8E93] text-[12px]">{action.description}</span>
              }
            />
          )
        })}
      </List>

      {/* Recent Maintenance History */}
      <BlockTitle>維護歷史</BlockTitle>
      <List strongIos insetIos>
        {mockHistory.map((item) => (
          <ListItem
            key={item.id}
            media={
              <div
                className={cn(
                  'w-10 h-10 rounded-xl flex items-center justify-center',
                  item.status === 'completed' ? 'bg-[#34C759]/15' : 'bg-[#FF3B30]/15',
                )}
              >
                {item.status === 'completed' ? (
                  <CheckCircle2 className="w-5 h-5 text-[#34C759]" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                )}
              </div>
            }
            title={<span className="font-medium text-sm">{item.action}</span>}
            subtitle={
              <span className="flex items-center gap-1 text-[#8E8E93] text-[12px]">
                <Clock className="w-3 h-3" />
                {formatRelativeTime(item.date)} &middot; 耗時 {item.duration}
              </span>
            }
            after={
              <span
                className="text-[11px] font-medium"
                style={{ color: item.status === 'completed' ? '#34C759' : '#FF3B30' }}
              >
                {item.status === 'completed' ? '完成' : '失敗'}
              </span>
            }
          />
        ))}
      </List>

      {/* Bottom padding */}
      <div className="h-8" />

      {/* Maintenance Mode Confirmation Dialog */}
      <Dialog
        opened={showMaintenanceDialog}
        onBackdropClick={() => setShowMaintenanceDialog(false)}
        title="啟用維護模式？"
        content={
          <p className="text-[13px] text-[#8E8E93] mt-2">
            所有玩家將被通知並無法進入遊戲。進行中的牌局將被允許完成。
          </p>
        }
        buttons={
          <>
            <DialogButton onClick={() => setShowMaintenanceDialog(false)}>
              取消
            </DialogButton>
            <DialogButton strong onClick={handleMaintenanceConfirm}>
              確認啟用
            </DialogButton>
          </>
        }
      />

      {/* System Action Confirmation Dialog */}
      <Dialog
        opened={showActionDialog}
        onBackdropClick={() => setShowActionDialog(false)}
        title={selectedAction?.label || ''}
        content={
          <p className="text-[13px] text-[#8E8E93] mt-2">
            {selectedAction?.confirmMessage}
          </p>
        }
        buttons={
          <>
            <DialogButton onClick={() => setShowActionDialog(false)}>
              取消
            </DialogButton>
            <DialogButton strong onClick={handleActionConfirm}>
              確認執行
            </DialogButton>
          </>
        }
      />
    </>
  )
}
